import {lazy, Suspense, useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {atomButtonDesigns, atomButtonShapes} from '@s-ui/react-atom-button'
import {ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import {useMountedState} from '@s-ui/react-hooks'
import IconArrowRight from '@adv-mt/mt-svg-icons/lib/ArrowRightMedium.js'
import TitleBasic, {titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {DRAWER_CONTENT_TYPE, RESERVATION_EVENTS_TRACKING} from '../utils/config.js'

const LazyDrawerReservationInitialization = lazy(() =>
  import(/* webpackChunkName: "LazyDrawerReservationInitialization" */ '@adv-mt/mt-drawer-reservation-initialization')
)

export const ReservationCardNewEntryPoint = ({
  ad,
  financingData,
  dealer,
  trackEvent,
  openReservationFaq = false,
  openReservationDrawer = false
}) => {
  const {i18n} = useContext(Context)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [drawerContent, setDrawerContent] = useState(DRAWER_CONTENT_TYPE.RESERVATION_INFO)
  const getMountedState = useMountedState()
  const isMounted = getMountedState()
  const {track} = useAnalyticsTrack()

  const adInfo = {
    id: ad?.id,
    title: ad?.title || '',
    imageUrl: ad?.imgUrl || '',
    images: ad?.photos || [],
    price: ad?.price || null,
    instalment: financingData?.fee || null,
    dealerRating: dealer?.ratings || null,
    dealerName: dealer?.name || '',
    year: ad?.year || null,
    km: ad?.km || null,
    fuelType: ad?.fuelType || null,
    imageCount: ad?.photos?.length || null
  }

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true)
    setDrawerContent(DRAWER_CONTENT_TYPE.RESERVATION_INFO)
    trackEvent({isReservationManage: false})
  }

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false)
  }

  const handleLinkClick = () => {
    setIsDrawerOpen(true)
    setDrawerContent(DRAWER_CONTENT_TYPE.RESERVATION_TERMS)
    track({
      event: RESERVATION_EVENTS_TRACKING.RESERVATION_MORE_INFORMATION_CLICKED,
      data: {hit_origin: 'detail', products: [{id: ad?.id, category: 'ads', price: 0, quantity: 0}]}
    })
  }

  const handleLegalConditionsClick = () => {
    setIsDrawerOpen(true)
    setDrawerContent(DRAWER_CONTENT_TYPE.RESERVATION_LEGAL_CONDITIONS)
  }

  useEffect(() => {
    if (openReservationFaq) {
      setIsDrawerOpen(true)
      setDrawerContent(DRAWER_CONTENT_TYPE.RESERVATION_TERMS)
    }
  }, [openReservationFaq])

  useEffect(() => {
    if (openReservationDrawer) {
      setIsDrawerOpen(true)
      setDrawerContent(DRAWER_CONTENT_TYPE.RESERVATION_INFO)
    }
  }, [openReservationDrawer])

  return (
    <div className="mt-PanelVehicleReservation-cardNewEntryPoint">
      <div className="mt-PanelVehicleReservation-cardContent">
        <div className="mt-PanelVehicleReservation-cardInfoWrapper">
          <TitleBasic
            as={titleBasicTags.h2}
            size={titleBasicSizes.xs}
            title="VEHICLE_RESERVATION.PANEL_SERVICE.TITLE_EXPERIMENT"
          />
          <p className="mt-PanelVehicleReservation-cardSubtitle">
            {i18n.t('VEHICLE_RESERVATION.PANEL_SERVICE.SUBTITLE_EXPERIMENT')}
          </p>
          <p className="mt-PanelVehicleReservation-cardSubtitle">
            <span className="mt-PanelVehicleReservation-cardSubtitle--refund">
              {i18n.t('VEHICLE_RESERVATION.PANEL_SERVICE.SUBTITLE_EXPERIMENT_REFUND')}
            </span>
            {i18n.t('VEHICLE_RESERVATION.PANEL_SERVICE.SUBTITLE_EXPERIMENT_REFUND_SUFFIX')}
          </p>
        </div>
        <div className="mt-PanelVehicleReservation-cardButtonWrapper">
          <AtomButton
            design={atomButtonDesigns.OUTLINE}
            fullWidth
            onClick={handleOpenDrawer}
            shape={atomButtonShapes.CIRCULAR}
            rightIcon={<IconArrowRight size={ATOM_ICON_SIZES.medium} />}
          >
            {i18n.t('VEHICLE_RESERVATION.PANEL_SERVICE.CTA_EXPERIMENT')}
          </AtomButton>
        </div>
      </div>
      {isMounted && isDrawerOpen && (
        <Suspense fallback={<></>}>
          <LazyDrawerReservationInitialization
            adInfo={adInfo}
            isDrawerOpen={isDrawerOpen}
            onCloseDrawer={handleCloseDrawer}
            drawerContent={drawerContent}
            handleLinkInfoClick={handleLinkClick}
            handleLegalConditionsClick={handleLegalConditionsClick}
            isNewReservationModuleV3
          />
        </Suspense>
      )}
    </div>
  )
}

ReservationCardNewEntryPoint.displayName = 'ReservationCardNewEntryPoint'
ReservationCardNewEntryPoint.propTypes = {
  ad: PropTypes.object,
  financingData: PropTypes.object,
  dealer: PropTypes.object,
  trackEvent: PropTypes.func,
  openReservationFaq: PropTypes.bool,
  openReservationDrawer: PropTypes.bool
}
